/**
 * ReservationDetailPage — route `/reservations/:id` (PMS portal).
 *
 * SCRUM-335 (booking reservation detail) — WIRED. This page is a thin
 * composition: it reads the `:id` route param and mounts the slice-internal
 * `<ReservationDetailView>` feature (the data-connected detail body), wiring
 * its `onBack` callback to the reservations grid route. It owns no state, no
 * data-fetching, and no business logic — the view drives `useReservationDetail`
 * + `useReservationLifecycle` + the property/type lookups internally.
 *
 * SCRUM-333 scaffolded this surface as an honest `NotConnectedYet` shell (the
 * route + permission gate `booking.reservations.read`). This ticket replaces
 * that placeholder body in place with the wired detail view; the route
 * manifest, the booking barrel, and the permission gate are UNCHANGED (per the
 * epic's "replace the shell in place" contract). The route already gates on
 * `booking.reservations.read` upstream (router.tsx) — this page does not
 * re-check.
 *
 * Navigation wiring:
 *   - `onBack()` → `/reservations` (the SCRUM-334 ReservationsPage grid).
 *
 * `useParams()` types the `:id` value as `string | undefined`; the route
 * guarantees the segment, but we guard the undefined edge (render only when
 * `id` is present) to satisfy the `reservationId: string` prop contract without
 * coercing an empty id into a request.
 */

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReservationDetailView } from '../features/ReservationDetailView.js';

export function ReservationDetailPage(): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (id === undefined) {
    return null;
  }

  return (
    <ReservationDetailView
      reservationId={id}
      onBack={() => navigate('/reservations')}
    />
  );
}
